import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { api } from '../lib/api';
import { formatDistance } from '../lib/distance';
import { supabase } from '../lib/supabase';
import type { CampScheduleSearchResult } from '../types/search';

interface SmartCampSelectorProps {
  value: string; // Selected camp ID
  onChange: (campId: string, campName: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  excludeCampIds?: string[]; // Optional: exclude specific camps
}

export default function SmartCampSelector({
  value,
  onChange,
  placeholder = 'Search camps...',
  className = '',
  required = false,
  excludeCampIds = []
}: SmartCampSelectorProps) {
  const session = useSession();
  const [query, setQuery] = useState('');
  const [selectedCampName, setSelectedCampName] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Get user location on mount (if available)
  useEffect(() => {
    if (!locationRequested && 'geolocation' in navigator) {
      setLocationRequested(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          // Silently fail in production - we'll just sort by name instead
          if (process.env.NODE_ENV === 'development') {
            console.log('Geolocation unavailable:', error.message);
          }
        },
        { timeout: 5000, maximumAge: 300000 } // 5s timeout, cache for 5 minutes
      );
    }
  }, [locationRequested]);

  // Debounced search query
  const [debouncedQuery, setDebouncedQuery] = useState('');
  
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query]);

  // Fetch camps based on search query
  const { data: searchResults, isLoading } = useQuery(
    ['camp-schedule-search', debouncedQuery, userLocation, session?.user?.id],
    async () => {
      const result = await api.camps.searchCampsForSchedule({
        query: debouncedQuery,
        userLocation: debouncedQuery.length >= 3 && userLocation ? userLocation : undefined,
        limit: 15,
        excludeCampIds
      });
      return result;
    },
    {
      enabled: isOpen, // Only fetch when dropdown is open
      staleTime: 1000 * 60, // 1 minute
    }
  );

  const camps = searchResults?.results || [];
  const isFavoritesOnly = searchResults?.isFavorites || false;

  // Track previous value to detect external changes
  const prevValueRef = useRef<string>(value);
  
  // Sync selectedCampName when value prop changes externally
  useEffect(() => {
    // Only sync if value changed externally (not from our own onChange)
    const valueChangedExternally = prevValueRef.current !== value;
    prevValueRef.current = value;
    
    if (value && !selectedCampName && !query && valueChangedExternally) {
      // Fetch camp name when value is set externally (e.g., when editing)
      const fetchCampName = async () => {
        try {
          const { data, error } = await supabase
            .from('camps')
            .select('name')
            .eq('id', value)
            .single();
          
          if (!error && data) {
            setSelectedCampName(data.name);
          }
        } catch (err) {
          console.error('Failed to fetch camp name:', err);
        }
      };
      
      void fetchCampName();
    } else if (!value && selectedCampName && valueChangedExternally) {
      // Clear selectedCampName when value is cleared externally (only if it changed externally)
      setSelectedCampName('');
    }
  }, [value, selectedCampName, query]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    
    // If user is typing and we have a selectedCampName, move it to query to allow editing
    if (selectedCampName && !query) {
      // User started typing after selecting a camp - allow editing
      setQuery(newQuery);
      setSelectedCampName('');
      // Clear the value since user is editing
      onChange('', '');
    } else {
      setQuery(newQuery);
      // If user clears the input completely, clear the value prop
      if (newQuery === '') {
        setSelectedCampName('');
        onChange('', '');
      }
    }
    
    setIsOpen(true);
    setSelectedIndex(-1);
    // Position will be updated by the useEffect that watches isOpen
  };

  // Handle camp selection
  const handleCampSelect = useCallback((camp: CampScheduleSearchResult) => {
    setQuery('');
    setSelectedCampName(camp.name); // Show selected camp name in input
    setIsOpen(false);
    setSelectedIndex(-1);
    setDropdownPosition(null);
    onChange(camp.id, camp.name);
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Tab key - close dropdown and allow normal tab navigation
    if (e.key === 'Tab') {
      setIsOpen(false);
      setSelectedIndex(-1);
      setDropdownPosition(null);
      // Don't prevent default - allow normal tab navigation
      return;
    }

    if (!isOpen) {
      if (e.key === 'Enter' && query === '') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (camps.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < camps.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < camps.length) {
          handleCampSelect(camps[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        setDropdownPosition(null);
        break;
    }
  };

  // Calculate dropdown position based on input position
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 320; // max-h-80 = 20rem = 320px
      const gap = 4; // 4px gap between input and dropdown
      
      // Check if there's enough space below, otherwise show above
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const showAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
      
      setDropdownPosition({
        top: showAbove 
          ? rect.top - dropdownMaxHeight - gap // Position above
          : rect.bottom + gap, // Position below (fixed positioning is relative to viewport)
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  // Update dropdown position when it opens or input position changes
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Calculate position immediately
      updateDropdownPosition();
      
      // Also try after a small delay to handle any layout shifts
      const timer = setTimeout(() => {
        if (isOpen && inputRef.current) {
          updateDropdownPosition();
        }
      }, 10);
      
      // Update position on scroll or resize
      const handleUpdate = () => {
        if (isOpen && inputRef.current) {
          updateDropdownPosition();
        }
      };
      
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    } else if (!isOpen) {
      // Clear position when closed
      setDropdownPosition(null);
    }
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside or losing focus (but not on Tab)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
        setDropdownPosition(null);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      // Close dropdown when input loses focus, but only if focus isn't moving to dropdown items
      if (
        inputRef.current &&
        event.target === inputRef.current &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.relatedTarget as Node)
      ) {
        // Small delay to allow clicks on dropdown items to register
        setTimeout(() => {
          if (inputRef.current && document.activeElement !== inputRef.current) {
            setIsOpen(false);
            setSelectedIndex(-1);
            setDropdownPosition(null);
          }
        }, 150);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      inputRef.current?.addEventListener('focusout', handleFocusOut);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        inputRef.current?.removeEventListener('focusout', handleFocusOut);
      };
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement && selectedElement.scrollIntoView) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query || selectedCampName}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsOpen(true);
            // Position will be calculated by useEffect when isOpen changes
          }}
          placeholder={placeholder}
          required={required && !value}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary shadow-sm"
          aria-label="Search for camps"
          aria-autocomplete="list"
          aria-controls="camp-listbox"
          aria-expanded={isOpen}
          role="combobox"
        />
      </div>

      {/* Dropdown Results - Rendered in Portal to appear outside modal */}
      {isOpen && dropdownPosition && typeof document !== 'undefined' && document.body && createPortal(
        <div
          ref={dropdownRef}
          id="camp-listbox"
          role="listbox"
          className="fixed bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none z-[10000]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxWidth: '100vw' // Ensure it doesn't overflow viewport width
          }}
        >
          {isLoading ? (
            <div className="px-4 py-3 text-gray-500 text-sm flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching camps...
            </div>
          ) : camps.length > 0 ? (
            <>
              {isFavoritesOnly && (
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                  Your Favorites
                </div>
              )}
              {camps.map((camp, index) => (
                <button
                  key={camp.id}
                  onClick={() => handleCampSelect(camp)}
                  role="option"
                  aria-selected={index === selectedIndex}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-start justify-between ${
                    index === selectedIndex ? 'bg-gray-100' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      {camp.is_favorite && (
                        <StarIconSolid className="h-4 w-4 text-yellow-400 flex-shrink-0" aria-label="Favorite" />
                      )}
                      <div className="font-medium text-gray-900 truncate">{camp.name}</div>
                    </div>
                    {camp.city && camp.state && (
                      <div className="text-gray-500 text-xs mt-0.5">
                        {camp.city}, {camp.state}
                      </div>
                    )}
                  </div>
                  {camp.distance_miles !== undefined && (
                    <div className="ml-2 flex-shrink-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {formatDistance(camp.distance_miles)}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </>
          ) : debouncedQuery.length >= 3 ? (
            <div className="px-4 py-3 text-gray-500 text-sm">
              No camps found matching &quot;{debouncedQuery}&quot;
            </div>
          ) : query.length > 0 && query.length < 3 ? (
            <div className="px-4 py-3 text-gray-500 text-sm">
              Type at least 3 characters to search
            </div>
          ) : session ? (
            <div className="px-4 py-3 text-gray-500 text-sm">
              No favorites yet. Start typing to search camps.
            </div>
          ) : (
            <div className="px-4 py-3 text-gray-500 text-sm">
              Type at least 3 characters to search camps
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
